"use client";
import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchReturnHistory, reset } from "@/state/sales/sale.slice";
import { fetchBusiness } from "@/state/business/business.slice";
import { getShops } from "@/state/shops/shop.slice";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import {
  Loader2,
  RefreshCcw,
  ArrowLeft,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Undo2,
  Package,
  Eye,
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { numberWithCommas } from "@/lib/formatCommas";
import { useDebounce } from "@/hooks/useDebounce";
import { todayInTimezone, daysAgoInTimezone } from "@/utils/formatDate";

export default function SaleReturnsPage() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { returns, returnsLoading, returnsPagination, returnsSummary, error } = useSelector((state) => state.sales);
  const { business } = useSelector((state) => state.business || {});
  const { shops } = useSelector((state) => state.shop || { shops: [] });
  const currentUser = useSelector((state) => state.auth.user);
  const tz = currentUser?.timezone || 'UTC';

  const businessDetails = business?.data || business;
  const currency = businessDetails?.currency || "GHS";

  // Pagination & filters
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [shopFilter, setShopFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [startDate, setStartDate] = useState(() => todayInTimezone(tz));
  const [endDate, setEndDate] = useState(() => todayInTimezone(tz));

  // Detail sheet
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);

  // Track if initial static data has been loaded
  const staticLoaded = useRef(false);

  useEffect(() => {
    if (!staticLoaded.current) {
      dispatch(fetchBusiness());
      dispatch(getShops());
      staticLoaded.current = true;
    }
  }, [dispatch]);

  const buildParams = useCallback(() => {
    const params = { page, limit };
    if (debouncedSearch) params.search = debouncedSearch;
    if (shopFilter) params.shopId = shopFilter;
    if (typeFilter !== "all") params.return_type = typeFilter;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return params;
  }, [page, limit, debouncedSearch, shopFilter, typeFilter, startDate, endDate]);

  useEffect(() => {
    dispatch(fetchReturnHistory(buildParams()));
  }, [dispatch, buildParams]);

  useEffect(() => {
    if (error) {
      toast({ variant: "destructive", title: "Error", description: error });
      dispatch(reset());
    }
  }, [error, toast, dispatch]);

  const handleViewDetails = (returnRecord) => {
    setSelectedReturn(returnRecord);
    setDetailSheetOpen(true);
  };

  const totalItems = returnsPagination?.total || 0;
  const totalPages = returnsPagination?.totalPages || 1;

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/dashboard/sales">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-xl font-bold">Sale Returns History</h1>
            <p className="text-sm text-muted-foreground">Track all returned products and refunds</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => dispatch(fetchReturnHistory(buildParams()))} disabled={returnsLoading}>
          <RefreshCcw className={`mr-2 h-4 w-4 ${returnsLoading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {returnsSummary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Returns</p>
              <p className="text-2xl font-bold">{returnsSummary.totalReturns}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Refunded</p>
              <p className="text-2xl font-bold text-red-600">{currency} {numberWithCommas(parseFloat(returnsSummary.totalRefunded || 0).toFixed(2))}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Items Returned</p>
              <p className="text-2xl font-bold">{returnsSummary.totalItemsReturned}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Compact Filters Bar */}
      <Card className="p-3">
        <div className="flex flex-wrap gap-2 items-center">
          {/* Search */}
          <div className="flex items-center space-x-2 bg-muted/30 px-2 rounded-md border flex-1 min-w-[200px] max-w-[300px]">
            <Search className="w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search returns..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              className="border-none shadow-none focus-visible:ring-0 h-8 bg-transparent"
            />
            {searchTerm && (
              <button onClick={() => { setSearchTerm(""); setPage(1); }} className="text-muted-foreground hover:text-foreground">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Type Filter */}
          <Select value={typeFilter} onValueChange={(val) => { setTypeFilter(val); setPage(1); }}>
            <SelectTrigger className="w-[110px] h-8 text-xs">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="full">Full</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
            </SelectContent>
          </Select>

          {/* Shop Filter */}
          {shops?.length > 0 && (
            <Select value={shopFilter} onValueChange={(val) => { setShopFilter(val === 'all' ? '' : val); setPage(1); }}>
              <SelectTrigger className="w-[120px] h-8 text-xs">
                <SelectValue placeholder="All Shops" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Shops</SelectItem>
                {shops.map((shop) => (
                  <SelectItem key={shop.id} value={shop.id.toString()}>
                    {shop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Date Range */}
          <div className="flex items-center gap-1 bg-muted/30 px-2 rounded-md border">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="w-[110px] h-7 bg-transparent border-none shadow-none focus-visible:ring-0 p-0 text-xs"
            />
            <span className="text-muted-foreground text-xs">to</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="w-[110px] h-7 bg-transparent border-none shadow-none focus-visible:ring-0 p-0 text-xs"
            />
          </div>

          {/* Quick Date Filters */}
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => {
              const today = todayInTimezone(tz);
              setStartDate(today); setEndDate(today); setPage(1);
            }}>Today</Button>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => {
              setStartDate(daysAgoInTimezone(7, tz)); setEndDate(todayInTimezone(tz)); setPage(1);
            }}>Week</Button>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => {
              setStartDate(daysAgoInTimezone(30, tz)); setEndDate(todayInTimezone(tz)); setPage(1);
            }}>Month</Button>
          </div>
        </div>
      </Card>

      {/* Returns Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Return #</TableHead>
                <TableHead>Sale #</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Refund</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Shop</TableHead>
                <TableHead>Processed By</TableHead>
                <TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {returnsLoading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-10">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : returns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-10">
                    <Undo2 className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">No returns found</p>
                  </TableCell>
                </TableRow>
              ) : (
                returns.map((ret) => (
                  <TableRow key={ret.id} className="cursor-pointer hover:bg-muted/50" onClick={() => handleViewDetails(ret)}>
                    <TableCell className="font-medium text-xs">{ret.return_number}</TableCell>
                    <TableCell className="text-xs">{ret.Sale?.order_number || "—"}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {ret.return_date ? format(new Date(ret.return_date), "dd MMM yyyy, hh:mm a") : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={ret.return_type === "full" ? "destructive" : "secondary"} className="text-xs">
                        {ret.return_type === "full" ? "Full" : "Partial"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">{ret.total_qty_returned}</TableCell>
                    <TableCell className="font-medium text-red-600 text-xs">
                      {currency} {numberWithCommas(parseFloat(ret.refund_amount || 0).toFixed(2))}
                    </TableCell>
                    <TableCell className="text-xs">{ret.refund_method}</TableCell>
                    <TableCell className="text-xs">{ret.shop?.name || "—"}</TableCell>
                    <TableCell className="text-xs">{ret.processedBy?.username || "—"}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); handleViewDetails(ret); }}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination — only show when total exceeds current limit */}
        {totalItems > limit && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Rows per page:</span>
              <Select value={limit.toString()} onValueChange={(val) => { setLimit(parseInt(val)); setPage(1); }}>
                <SelectTrigger className="w-[70px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                {totalItems > 0
                  ? `${(page - 1) * limit + 1}-${Math.min(page * limit, totalItems)} of ${totalItems}`
                  : "0 items"}
              </span>
              <div className="flex gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(p => p - 1)} disabled={page <= 1 || returnsLoading}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages || returnsLoading}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Return Detail Sheet */}
      <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Return Details</SheetTitle>
          </SheetHeader>
          {selectedReturn && (
            <div className="space-y-5 mt-4">
              {/* Return Info */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Return #</span>
                  <span className="font-medium">{selectedReturn.return_number}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Date</span>
                  <span>{selectedReturn.return_date ? format(new Date(selectedReturn.return_date), "dd MMM yyyy, hh:mm a") : "—"}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Type</span>
                  <Badge variant={selectedReturn.return_type === "full" ? "destructive" : "secondary"}>
                    {selectedReturn.return_type === "full" ? "Full Return" : "Partial Return"}
                  </Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Processed By</span>
                  <span>{selectedReturn.processedBy?.username || "—"}</span>
                </div>
                {selectedReturn.shop && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Shop</span>
                    <span>{selectedReturn.shop.name}</span>
                  </div>
                )}
                {selectedReturn.reason && (
                  <div className="text-sm">
                    <span className="text-muted-foreground">Reason:</span>
                    <p className="mt-1 text-sm bg-muted p-2 rounded">{selectedReturn.reason}</p>
                  </div>
                )}
              </div>

              {/* Sale Info */}
              {selectedReturn.Sale && (
                <div className="border rounded-lg p-3 space-y-2">
                  <p className="text-sm font-medium">Original Sale</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Order #</span>
                    <span className="font-medium">{selectedReturn.Sale.order_number}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Sale Total</span>
                    <span>{currency} {numberWithCommas(parseFloat(selectedReturn.Sale.total || 0).toFixed(2))}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Amount Paid</span>
                    <span>{currency} {numberWithCommas(parseFloat(selectedReturn.Sale.amount_paid || 0).toFixed(2))}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Sale Status</span>
                    <Badge variant="outline">{selectedReturn.Sale.status}</Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Payment Status</span>
                    <Badge variant="outline">{selectedReturn.Sale.payment_status}</Badge>
                  </div>
                  {selectedReturn.Sale.Customer && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Customer</span>
                      <span>{selectedReturn.Sale.Customer.name}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Returned Items */}
              <div>
                <p className="text-sm font-medium mb-2">Returned Items</p>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead className="text-center w-16">Qty</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(selectedReturn.items || []).map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="text-sm">
                            <div className="flex items-center gap-2">
                              {item.Product?.feature_image ? (
                                <img src={item.Product.feature_image} alt="" className="h-8 w-8 rounded object-cover" />
                              ) : (
                                <div className="h-8 w-8 rounded bg-muted flex items-center justify-center">
                                  <Package className="h-4 w-4 text-muted-foreground" />
                                </div>
                              )}
                              <span>{item.product_name || item.Product?.title || "—"}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">{item.quantity_returned}</TableCell>
                          <TableCell className="text-right text-xs">{currency} {numberWithCommas(parseFloat(item.unit_price || 0).toFixed(2))}</TableCell>
                          <TableCell className="text-right text-xs font-medium">{currency} {numberWithCommas(parseFloat(item.total || 0).toFixed(2))}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Refund Summary */}
              <div className="border rounded-lg p-3 bg-red-50 dark:bg-red-900/10 space-y-2">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">Refund Summary</p>
                <div className="flex justify-between text-sm">
                  <span className="text-red-700 dark:text-red-300">Refund Amount</span>
                  <span className="font-bold text-red-700 dark:text-red-300">{currency} {numberWithCommas(parseFloat(selectedReturn.refund_amount || 0).toFixed(2))}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-red-700 dark:text-red-300">Refund Method</span>
                  <span className="text-red-700 dark:text-red-300">{selectedReturn.refund_method}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-red-700 dark:text-red-300">Total Items Returned</span>
                  <span className="text-red-700 dark:text-red-300">{selectedReturn.total_qty_returned}</span>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
